import { useEffect, useState, useCallback } from "react";
import { Material, Subject, AppUser } from "../serverlib/util-types";
import { getMaterialsWithIds, renameMaterialById, deleteMaterialById, updateMaterialLinkById, getAppUser } from "../serverlib/db";
import { useTranslation } from "react-i18next";
import { User } from "firebase/auth";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface MaterialRow extends Material {
    _id: string;
}

interface DeleteModalProps {
    name: string;
    onConfirm: () => void;
    onCancel: () => void;
}
const DeleteModal = ({ name, onConfirm, onCancel }: DeleteModalProps) => {
    const { t } = useTranslation("common");
    const [input, setInput] = useState('');
    const keyword = t("admin.delete-modal-keyword");
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.55)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: '16px'
        }} onClick={onCancel}>
            <div style={{
                background: 'var(--grey-secondary)', borderRadius: 12,
                padding: '24px 28px', maxWidth: 420, width: '100%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
            }} onClick={e => e.stopPropagation()}>
                <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                    {t("admin.delete-modal-title", { name })}
                </p>
                <p style={{ fontSize: 13, marginBottom: 16, opacity: 0.75 }}
                    dangerouslySetInnerHTML={{ __html: t("admin.delete-modal-body") }} />
                <input
                    className="admin-input"
                    type="text"
                    autoFocus
                    placeholder={t("admin.delete-modal-placeholder")}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && input === keyword) onConfirm(); }}
                    style={{ width: '100%', marginBottom: 16, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="admin-button hover-up" type="button" onClick={onCancel}>
                        {t("admin.delete-modal-cancel")}
                    </button>
                    <button
                        className="admin-button hover-up"
                        type="button"
                        disabled={input !== keyword}
                        onClick={onConfirm}
                        style={{
                            background: input === keyword ? 'var(--error-color, #e05252)' : 'var(--grey-tertiary)',
                            color: 'var(--text-primary)',
                            opacity: input === keyword ? 1 : 0.5,
                            transition: 'background 0.15s, opacity 0.15s'
                        }}
                    >
                        {t("admin.delete-modal-confirm")}
                    </button>
                </div>
            </div>
        </div>
    );
};

interface MaterialsListProps {
    user: User | null;
    subjectfromurl: string | null;
    gradefromurl: string | null;
}

const MaterialsList = ({ user, subjectfromurl, gradefromurl }: MaterialsListProps) => {
    const { t } = useTranslation("common");
    const [appUser, setAppUser] = useState<AppUser | null>(null);
    const [materials, setMaterials] = useState<MaterialRow[]>([]);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
    const [linkValue, setLinkValue] = useState('');
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (user?.uid) getAppUser(user.uid).then(u => setAppUser(u));
    }, [user]);

    const isPrivileged = appUser && (appUser.isAdmin || appUser.accesslevel === "teacher");

    const fetchMaterials = useCallback(async () => {
        if (!subjectfromurl || !gradefromurl) return;
        // Materials are stored under the plain grade key (e.g. "9", "Prep") — same as getMaterials()
        const rows = await getMaterialsWithIds(subjectfromurl as Subject, gradefromurl);
        setMaterials(rows);
    }, [subjectfromurl, gradefromurl]);

    useEffect(() => {
        if (isVisible) fetchMaterials();
    }, [isVisible, fetchMaterials]);

    const toggleExpand = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const startRename = (mat: MaterialRow) => {
        setRenamingId(mat._id);
        setRenameValue(mat.name);
    };

    const startEditLink = (mat: MaterialRow) => {
        setEditingLinkId(mat._id);
        setLinkValue(mat.content);
    };

    const saveLink = async (mat: MaterialRow) => {
        const trimmed = linkValue.trim();
        if (trimmed && trimmed !== mat.content) {
            const ok = await updateMaterialLinkById(subjectfromurl as Subject, gradefromurl!, mat._id, trimmed);
            if (ok) setMaterials(prev => prev.map(m => m._id === mat._id ? { ...m, content: trimmed } : m));
        }
        setEditingLinkId(null);
    };

    const saveRename = async (mat: MaterialRow) => {
        const trimmed = renameValue.trim();
        if (trimmed && trimmed !== mat.name) {
            const ok = await renameMaterialById(subjectfromurl as Subject, gradefromurl!, mat._id, trimmed);
            if (ok) setMaterials(prev => prev.map(m => m._id === mat._id ? { ...m, name: trimmed } : m));
        }
        setRenamingId(null);
    };

    const confirmDelete = async () => {
        if (!pendingDeleteId) return;
        const ok = await deleteMaterialById(subjectfromurl as Subject, gradefromurl!, pendingDeleteId);
        if (ok) setMaterials(prev => prev.filter(m => m._id !== pendingDeleteId));
        setPendingDeleteId(null);
    };

    if (!isPrivileged || !subjectfromurl || !gradefromurl) return null;

    const pendingMat = pendingDeleteId ? materials.find(m => m._id === pendingDeleteId) : null;
    const nonTextMats = materials.filter(
        mat => mat.materialtype !== "text"
    );
    return (
        <div className="admin-widget">
            <div className="admin-widget-title" onClick={() => setIsVisible(v => !v)}>
                <b>📋 {t("admin.all-materials")}</b>
            </div>
            <div className={`admin-widget-content ${isVisible ? 'visible' : ''}`}>
                <hr />
                <div className="admin-subwidget">
                    {nonTextMats.length === 0 && isVisible && (
                        <p style={{ opacity: 0.6, fontSize: 13 }}>{t("misc.nomatch")}</p>
                    )}
                    {nonTextMats.map(mat => {
                        const expanded = expandedIds.has(mat._id);
                        const renaming = renamingId === mat._id;
                        return (
                            <div key={mat._id} className="mat-list-row">
                                {/* Compact header row */}
                                <div className="mat-list-header">
                                    {renaming ? (
                                        <>
                                            <input
                                                className="admin-input mat-list-rename-input"
                                                type="text"
                                                autoFocus
                                                value={renameValue}
                                                onChange={e => setRenameValue(e.target.value)}
                                                onKeyDown={e => {
                                                    e.stopPropagation();
                                                    if (e.key === 'Enter') saveRename(mat);
                                                    if (e.key === 'Escape') setRenamingId(null);
                                                }}
                                            />
                                            <button
                                                className="mat-list-btn mat-list-save-btn hover-up"
                                                type="button"
                                                onClick={() => saveRename(mat)}
                                                title={t("admin.rename-material-save")}
                                            >✓</button>
                                            <button
                                                className="mat-list-btn hover-up"
                                                type="button"
                                                onClick={() => setRenamingId(null)}
                                                title={t("admin.delete-modal-cancel")}
                                            >✕</button>
                                        </>
                                    ) : (
                                        <span className="mat-list-name" title={mat.name}>{mat.name}</span>
                                    )}
                                    {!renaming && <span className="mat-list-type">{mat.materialtype}</span>}
                                    <div className="mat-list-actions">
                                        {!renaming && <button
                                            className="mat-list-btn hover-up"
                                            type="button"
                                            title={t("admin.mat-rename")}
                                            onClick={e => { (e.currentTarget as HTMLButtonElement).blur(); startRename(mat); }}
                                        >
                                            <EditIcon style={{ fontSize: 15 }} />
                                        </button>}
                                        {!renaming && <button
                                            className="mat-list-btn hover-up"
                                            type="button"
                                            title={t("admin.mat-delete")}
                                            onClick={() => setPendingDeleteId(mat._id)}
                                            style={{ color: 'var(--error-color, #e05252)' }}
                                        >
                                            <DeleteIcon style={{ fontSize: 15 }} />
                                        </button>}
                                        {!renaming && <button
                                            className="mat-list-btn hover-up"
                                            type="button"
                                            title={expanded ? t("admin.mat-collapse") : t("admin.mat-expand")}
                                            onClick={() => toggleExpand(mat._id)}
                                        >
                                            {expanded ? <ExpandLessIcon style={{ fontSize: 15 }} /> : <ExpandMoreIcon style={{ fontSize: 15 }} />}
                                        </button>}
                                    </div>
                                </div>
                                {/* Expanded preview */}
                                {expanded && (
                                    <div className="mat-list-expanded">
                                        <p className="mat-list-meta">
                                            <b>{t("admin.topics")}:</b> {mat.topic || '—'}
                                        </p>
                                        {/* Link editor */}
                                        {editingLinkId === mat._id ? (
                                            <div className="mat-list-link-editor">
                                                <input
                                                    className="admin-input mat-list-link-input"
                                                    type="url"
                                                    autoFocus
                                                    value={linkValue}
                                                    onChange={e => setLinkValue(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') saveLink(mat);
                                                        if (e.key === 'Escape') setEditingLinkId(null);
                                                    }}
                                                />
                                                <button
                                                    className="mat-list-btn mat-list-save-btn hover-up"
                                                    type="button"
                                                    onClick={() => saveLink(mat)}
                                                    title={t("admin.rename-material-save")}
                                                >✓</button>
                                                <button
                                                    className="mat-list-btn hover-up"
                                                    type="button"
                                                    onClick={() => setEditingLinkId(null)}
                                                    title={t("admin.delete-modal-cancel")}
                                                >✕</button>
                                            </div>
                                        ) : (
                                            <div className="mat-list-link-row">
                                                <a href={mat.content} target="_blank" rel="noreferrer" className="mat-list-link">
                                                    {mat.content.length > 60 ? mat.content.slice(0, 60) + '…' : mat.content}
                                                </a>
                                                <button
                                                    className="mat-list-btn hover-up"
                                                    type="button"
                                                    onClick={() => startEditLink(mat)}
                                                    title={t("admin.mat-rename")}
                                                ><EditIcon style={{ fontSize: 13 }} /></button>
                                            </div>
                                        )}
                                        {mat.materialtype !== 'text' && mat.content && (
                                            <div className="mat-list-preview">
                                                <iframe
                                                    src={editingLinkId === mat._id ? mat.content : mat.content}
                                                    title={mat.name}
                                                    style={{ width: '100%', height: 320, border: 'none', borderRadius: 6 }}
                                                    allowFullScreen
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            {pendingMat && (
                <DeleteModal
                    name={pendingMat.name}
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDeleteId(null)}
                />
            )}
        </div>
    );
};

export default MaterialsList;
